/**
 * Engineering Ladder Storage
 * IndexedDB persistence for career ladder frameworks
 */

import type { LadderFramework } from './types';

const DB_NAME = 'engineering-ladder-db';
const DB_VERSION = 1;
const STORE_NAME = 'frameworks';

let dbInstance: IDBDatabase | null = null;

/**
 * Initialize IndexedDB
 */
export async function initDB(): Promise<IDBDatabase> {
  if (dbInstance) return dbInstance;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[EngineeringLadder] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create frameworks store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('name', 'name', { unique: false });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
  });
}

/**
 * Get all saved frameworks
 */
export async function getAllFrameworks(): Promise<LadderFramework[]> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      // Sort by updatedAt descending (most recent first)
      const frameworks = request.result.sort((a, b) => b.updatedAt - a.updatedAt);
      resolve(frameworks);
    };

    request.onerror = () => {
      console.error('[EngineeringLadder] Failed to get frameworks:', request.error);
      reject(request.error);
    };
  });
}

/**
 * Get a single framework by ID
 */
export async function getFramework(id: string): Promise<LadderFramework | null> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => {
      resolve(request.result || null);
    };

    request.onerror = () => {
      console.error('[EngineeringLadder] Failed to get framework:', request.error);
      reject(request.error);
    };
  });
}

/**
 * Save a framework (create or update)
 */
export async function saveFramework(framework: LadderFramework): Promise<void> {
  const db = await initDB();

  // Update timestamp
  const updatedFramework = {
    ...framework,
    updatedAt: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(updatedFramework);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      console.error('[EngineeringLadder] Failed to save framework:', request.error);
      reject(request.error);
    };
  });
}

/**
 * Delete a framework by ID
 */
export async function deleteFramework(id: string): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      console.error('[EngineeringLadder] Failed to delete framework:', request.error);
      reject(request.error);
    };
  });
}

/**
 * Create a new framework from a template
 */
export async function createFromTemplate(
  templateFramework: Omit<LadderFramework, 'id' | 'createdAt' | 'updatedAt'>,
  name?: string
): Promise<LadderFramework> {
  const now = Date.now();
  const id = `framework-${now}-${Math.random().toString(36).substr(2, 9)}`;

  const framework: LadderFramework = {
    ...templateFramework,
    id,
    name: name || templateFramework.name,
    createdAt: now,
    updatedAt: now,
  };

  await saveFramework(framework);
  return framework;
}

/**
 * Duplicate an existing framework
 */
export async function duplicateFramework(
  id: string,
  newName?: string
): Promise<LadderFramework | null> {
  const original = await getFramework(id);
  if (!original) return null;

  const now = Date.now();
  const newId = `framework-${now}-${Math.random().toString(36).substr(2, 9)}`;

  const duplicate: LadderFramework = {
    ...original,
    id: newId,
    name: newName || `${original.name} (Copy)`,
    createdAt: now,
    updatedAt: now,
  };

  await saveFramework(duplicate);
  return duplicate;
}

/**
 * Export framework to JSON
 */
export function exportToJSON(framework: LadderFramework): string {
  return JSON.stringify(framework, null, 2);
}

/**
 * Import framework from JSON
 */
export async function importFromJSON(json: string): Promise<LadderFramework> {
  const parsed = JSON.parse(json) as LadderFramework;

  // Generate new IDs to avoid conflicts
  const now = Date.now();
  const newId = `framework-${now}-${Math.random().toString(36).substr(2, 9)}`;

  const framework: LadderFramework = {
    ...parsed,
    id: newId,
    name: parsed.name || 'Imported Framework',
    createdAt: now,
    updatedAt: now,
  };

  await saveFramework(framework);
  return framework;
}

/**
 * Export framework to Markdown
 */
export function exportToMarkdown(framework: LadderFramework): string {
  const lines: string[] = [];

  lines.push(`# ${framework.name}`);
  lines.push('');
  if (framework.description) {
    lines.push(framework.description);
    lines.push('');
  }

  // Get competencies map for quick lookup
  const competencyMap = new Map(
    framework.competencies.map(c => [c.id, c])
  );

  // Export each track
  for (const trackType of ['ic', 'management'] as const) {
    const track = framework.tracks[trackType];
    lines.push(`## ${track.name}`);
    lines.push('');
    if (track.description) {
      lines.push(track.description);
      lines.push('');
    }

    // Export each level
    for (const level of track.levels) {
      lines.push(`### ${level.code}: ${level.title}`);
      lines.push('');

      if (level.typicalYearsExperience) {
        lines.push(`**Typical Experience:** ${level.typicalYearsExperience} years`);
        lines.push('');
      }

      if (level.scope) {
        lines.push(`**Scope:** ${level.scope}`);
        lines.push('');
      }

      if (level.impact) {
        lines.push(`**Impact:** ${level.impact}`);
        lines.push('');
      }

      if (level.salaryBandMin || level.salaryBandMid || level.salaryBandMax) {
        const currency = framework.currency || 'USD';
        const parts: string[] = [];
        if (level.salaryBandMin) parts.push(`Min: ${currency} ${level.salaryBandMin.toLocaleString()}`);
        if (level.salaryBandMid) parts.push(`Mid: ${currency} ${level.salaryBandMid.toLocaleString()}`);
        if (level.salaryBandMax) parts.push(`Max: ${currency} ${level.salaryBandMax.toLocaleString()}`);
        lines.push(`**Salary Band:** ${parts.join(' | ')}`);
        lines.push('');
      }

      if (level.responsibilities && level.responsibilities.length > 0) {
        lines.push('**Responsibilities:**');
        for (const resp of level.responsibilities) {
          lines.push(`- ${resp}`);
        }
        lines.push('');
      }

      if (level.progressionCriteria && level.progressionCriteria.length > 0) {
        lines.push('**Progression Criteria:**');
        for (const criteria of level.progressionCriteria) {
          lines.push(`- ${criteria}`);
        }
        lines.push('');
      }

      if (level.competencies && level.competencies.length > 0) {
        lines.push('**Expected Competencies:**');
        lines.push('');
        lines.push('| Competency | Expected Level |');
        lines.push('|------------|----------------|');
        for (const lc of level.competencies) {
          const competency = competencyMap.get(lc.competencyId);
          if (competency) {
            lines.push(`| ${competency.name} | ${lc.expectedLevel} |`);
          }
        }
        lines.push('');
      }

      lines.push('---');
      lines.push('');
    }
  }

  // Export competency definitions
  lines.push('## Competency Definitions');
  lines.push('');

  const categoryGroups = new Map<string, typeof framework.competencies>();
  for (const comp of framework.competencies) {
    if (!categoryGroups.has(comp.category)) {
      categoryGroups.set(comp.category, []);
    }
    categoryGroups.get(comp.category)!.push(comp);
  }

  for (const [category, competencies] of categoryGroups) {
    lines.push(`### ${category.charAt(0).toUpperCase() + category.slice(1)}`);
    lines.push('');
    for (const comp of competencies) {
      lines.push(`**${comp.name}**`);
      lines.push('');
      lines.push(comp.description);
      lines.push('');
      if (comp.examples && comp.examples.length > 0) {
        lines.push('Examples:');
        for (const ex of comp.examples) {
          lines.push(`- ${ex}`);
        }
        lines.push('');
      }
    }
  }

  return lines.join('\n');
}

/**
 * Get count of saved frameworks
 */
export async function getFrameworkCount(): Promise<number> {
  const frameworks = await getAllFrameworks();
  return frameworks.length;
}

/**
 * Clear all frameworks (use with caution)
 */
export async function clearAllFrameworks(): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      console.error('[EngineeringLadder] Failed to clear frameworks:', request.error);
      reject(request.error);
    };
  });
}
